import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  TextField,
  IconButton,
  CircularProgress,
  Checkbox,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Lock as LockIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import {
  tienePermisosModulo,
  puedeEditarProgramaContabilidad,
} from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx, StatusPill } from 'components/financepro';
import PrintConciliacion from './PrintConciliacion';

export const PROGRAMA_CONCILIACION = 'conciliacionbancaria';

const cardShellSx = {
  borderRadius: 2.5,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  border: `1px solid ${FP.color.border}`,
  overflow: 'hidden',
};

const botonNavySx = {
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: 1.4,
  bgcolor: FP.color.navy,
  color: '#FFFFFF',
  boxShadow: 'none',
  '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
  '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
};

const botonOutlinedSx = {
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: 1.4,
  borderColor: FP.color.border,
  color: FP.color.textPrimary,
  '&:hover': { borderColor: FP.color.borderStrong, bgcolor: FP.color.surfaceAlt },
};

const statusVariant = (s) => {
  const m = {
    Pendiente: 'warning',
    'En proceso': 'info',
    'Con diferencias': 'danger',
    Conciliada: 'success',
    Anulada: 'neutral',
  };
  return m[s] || 'neutral';
};

const fmtMoney = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const cellHeadSx = {
  fontWeight: 700,
  fontSize: '0.75rem',
  color: FP.color.textSecondary,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  display: 'block',
  mb: 0.5,
};

const cellEllipsisSx = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

/**
 * Detalle de una conciliación bancaria: muestra las transacciones con
 * checkboxes para conciliar y permite guardar, cerrar o anular.
 */
const ConciliacionBancaria = ({ conciliacionId, onVolver }) => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { usuarioLog, claveLog, roleDetalles, nombreNegocio } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const puedeModificar = useMemo(
    () => puedeEditarProgramaContabilidad(roleDetalles, PROGRAMA_CONCILIACION),
    [roleDetalles]
  );

  const puedeOperar = Boolean(selectedCompany?.db_empresa && usuarioLog && claveLog);

  const [encabezado, setEncabezado] = useState(null);
  const [detalles, setDetalles] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [balanceBanco, setBalanceBanco] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [dialogoCerrar, setDialogoCerrar] = useState(false);
  const [dialogoAnular, setDialogoAnular] = useState(false);

  const credencialesBase = useCallback(
    () => ({
      usuario: usuarioLog || '',
      password: claveLog || '',
      id_empresa: selectedCompany,
    }),
    [usuarioLog, claveLog, selectedCompany]
  );

  const cargar = useCallback(async () => {
    if (!puedeOperar || !conciliacionId) return;
    setCargando(true);
    try {
      const res = await fetch(`${servidor}/api/conciliacion_obtener`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...credencialesBase(), conciliacion_id: conciliacionId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.Error || 'Error al cargar conciliación');
      setEncabezado(data.conciliacion || null);
      setDetalles(data.detalles || []);
      setBalanceBanco(String(data.conciliacion?.balance_banco ?? '0'));
      setObservaciones(data.conciliacion?.observaciones || '');
    } catch (e) {
      showError(e.message);
    } finally {
      setCargando(false);
    }
  }, [puedeOperar, conciliacionId, servidor, credencialesBase, showError]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const esEditable = encabezado && !['Conciliada', 'Anulada'].includes(encabezado.status);

  const togglePartida = async (detId, conciliar) => {
    if (!esEditable || !puedeModificar) return;
    try {
      const res = await fetch(`${servidor}/api/conciliacion_togglepartida`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...credencialesBase(),
          detalle_id: detId,
          conciliar,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.Error || 'Error al conciliar partida');

      setDetalles((prev) =>
        prev.map((d) =>
          d.id === detId ? { ...d, conciliado: conciliar ? 'S' : 'N' } : d
        )
      );
      setEncabezado((prev) => ({
        ...prev,
        balance_conciliado: data.balance_conciliado,
        diferencia: data.diferencia,
        status: data.status,
      }));
    } catch (e) {
      showError(e.message);
    }
  };

  const toggleAll = async (conciliar) => {
    if (!esEditable || !puedeModificar || detalles.length === 0) return;
    const ids = detalles
      .filter((d) => (conciliar ? d.conciliado !== 'S' : d.conciliado === 'S'))
      .map((d) => d.id);
    if (ids.length === 0) return;

    try {
      const res = await fetch(`${servidor}/api/conciliacion_togglemultiple`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...credencialesBase(),
          conciliacion_id: conciliacionId,
          ids_detalle: ids,
          conciliar,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.Error || 'Error');

      setDetalles((prev) =>
        prev.map((d) =>
          ids.includes(d.id) ? { ...d, conciliado: conciliar ? 'S' : 'N' } : d
        )
      );
      setEncabezado((prev) => ({
        ...prev,
        balance_conciliado: data.balance_conciliado,
        diferencia: data.diferencia,
        status: data.status,
      }));
    } catch (e) {
      showError(e.message);
    }
  };

  const guardar = async () => {
    if (!esEditable || !puedeModificar) return;
    setGuardando(true);
    try {
      const res = await fetch(`${servidor}/api/conciliacion_guardar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...credencialesBase(),
          conciliacion_id: conciliacionId,
          balance_banco: parseFloat(balanceBanco) || 0,
          observaciones,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.Error || 'Error al guardar');
      showSuccess('Conciliación guardada');
      cargar();
    } catch (e) {
      showError(e.message);
    } finally {
      setGuardando(false);
    }
  };

  const cerrar = async () => {
    setDialogoCerrar(false);
    setGuardando(true);
    try {
      const res = await fetch(`${servidor}/api/conciliacion_cerrar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...credencialesBase(),
          conciliacion_id: conciliacionId,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.Error || 'Error al cerrar');
      showSuccess(`Conciliación cerrada con status: ${data.status}`);
      cargar();
    } catch (e) {
      showError(e.message);
    } finally {
      setGuardando(false);
    }
  };

  const anular = async () => {
    setDialogoAnular(false);
    setGuardando(true);
    try {
      const res = await fetch(`${servidor}/api/conciliacion_anular`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...credencialesBase(),
          conciliacion_id: conciliacionId,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.Error || 'Error al anular');
      showSuccess('Conciliación anulada');
      cargar();
    } catch (e) {
      showError(e.message);
    } finally {
      setGuardando(false);
    }
  };

  const allChecked = detalles.length > 0 && detalles.every((d) => d.conciliado === 'S');
  const someChecked = detalles.some((d) => d.conciliado === 'S');

  const totalDebitos = detalles.reduce((s, d) => s + Number(d.debito || 0), 0);
  const totalCreditos = detalles.reduce((s, d) => s + Number(d.credito || 0), 0);
  const totalDebConc = detalles.filter((d) => d.conciliado === 'S').reduce((s, d) => s + Number(d.debito || 0), 0);
  const totalCredConc = detalles.filter((d) => d.conciliado === 'S').reduce((s, d) => s + Number(d.credito || 0), 0);

  if (cargando && !encabezado) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: FP.color.surface, p: 3, display: 'flex', justifyContent: 'center', pt: 10 }}>
        <CircularProgress sx={{ color: FP.color.navy }} />
      </Box>
    );
  }

  if (!encabezado) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: FP.color.surface, p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={onVolver} sx={botonOutlinedSx}>
          Volver
        </Button>
        <Typography sx={{ mt: 2, color: FP.color.textSecondary }}>
          No se encontró la conciliación.
        </Typography>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface, p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={onVolver} sx={botonOutlinedSx} size="small">
          Volver
        </Button>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: FP.color.textPrimary }}>
            Conciliación #{encabezado.numero}
          </Typography>
          <Typography sx={{ fontSize: '0.82rem', color: FP.color.textSecondary }}>
            {encabezado.cuenta_banco} · {encabezado.fecha_inicio} al {encabezado.fecha_fin}
          </Typography>
        </Box>
        <StatusPill label={encabezado.status} variant={statusVariant(encabezado.status)} />
      </Box>

      {/* Resumen de saldos */}
      <Card sx={{ ...cardShellSx, mb: 2 }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <Box sx={{ flex: '1 1 180px' }}>
              <Typography sx={{ fontSize: '0.72rem', color: FP.color.textSecondary, fontWeight: 600, textTransform: 'uppercase', mb: 0.3 }}>
                Saldo anterior
              </Typography>
              <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: FP.color.textPrimary }}>
                {fmtMoney(encabezado.balance_inicial)}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: FP.color.textMuted }}>
                Libros antes de {encabezado.fecha_inicio}
              </Typography>
            </Box>
            <Box sx={{ flex: '1 1 180px' }}>
              <Typography sx={{ fontSize: '0.72rem', color: FP.color.textSecondary, fontWeight: 600, textTransform: 'uppercase', mb: 0.3 }}>
                Balance Libro
              </Typography>
              <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: FP.color.textPrimary }}>
                {fmtMoney(encabezado.balance_libro)}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: FP.color.textMuted }}>
                Saldo anterior + período
              </Typography>
            </Box>
            <Box sx={{ flex: '1 1 200px' }}>
              <TextField
                label="Balance Banco (Estado de Cuenta)"
                value={balanceBanco}
                onChange={(e) => setBalanceBanco(e.target.value)}
                disabled={!esEditable || !puedeModificar}
                size="small"
                type="number"
                InputLabelProps={{ shrink: true }}
                sx={{ ...fpInputSx, minWidth: 200 }}
              />
            </Box>
            <Box sx={{ flex: '1 1 180px' }}>
              <Typography sx={{ fontSize: '0.72rem', color: FP.color.textSecondary, fontWeight: 600, textTransform: 'uppercase', mb: 0.3 }}>
                Balance Conciliado
              </Typography>
              <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: FP.color.accentBlue }}>
                {fmtMoney(encabezado.balance_conciliado)}
              </Typography>
            </Box>
            <Box sx={{ flex: '1 1 180px' }}>
              <Typography sx={{ fontSize: '0.72rem', color: FP.color.textSecondary, fontWeight: 600, textTransform: 'uppercase', mb: 0.3 }}>
                Diferencia
              </Typography>
              <Typography
                sx={{
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  color: Math.abs(Number(encabezado.diferencia || 0)) < 0.01 ? FP.color.success : FP.color.danger,
                }}
              >
                {fmtMoney(encabezado.diferencia)}
              </Typography>
            </Box>
          </Box>

          <TextField
            fullWidth
            label="Observaciones"
            multiline
            rows={2}
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            disabled={!esEditable || !puedeModificar}
            InputLabelProps={{ shrink: true }}
            sx={{ ...fpInputSx, mt: 2 }}
          />

          {/* Acciones */}
          <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <PrintConciliacion
              conciliacion={encabezado}
              detalles={detalles}
              empresa={{ nombre: nombreNegocio }}
              buttonProps={{ size: 'small', sx: botonOutlinedSx }}
            />
            {esEditable && puedeModificar && (
              <>
                <Button
                  variant="contained"
                  startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  onClick={guardar}
                  disabled={guardando}
                  size="small"
                  sx={botonNavySx}
                >
                  Guardar
                </Button>
                <Button
                  variant="contained"
                  startIcon={<CheckCircleIcon />}
                  onClick={() => setDialogoCerrar(true)}
                  disabled={guardando}
                  size="small"
                  sx={{ ...botonNavySx, bgcolor: FP.color.success, '&:hover': { bgcolor: FP.color.successDark } }}
                >
                  Cerrar conciliación
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={() => setDialogoAnular(true)}
                  disabled={guardando}
                  size="small"
                  sx={{ ...botonOutlinedSx, borderColor: FP.color.danger, color: FP.color.danger, '&:hover': { bgcolor: FP.color.dangerSoft } }}
                >
                  Anular
                </Button>
              </>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Tabla de partidas */}
      <Card sx={cardShellSx}>
        <CardContent sx={{ p: { xs: 1, md: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: FP.color.textPrimary }}>
              Partidas ({detalles.length})
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Chip
                size="small"
                label={`Conciliadas: ${detalles.filter((d) => d.conciliado === 'S').length}`}
                sx={{ bgcolor: FP.color.successSoft, color: FP.color.successDark, fontWeight: 700, fontSize: '0.7rem' }}
              />
              <Chip
                size="small"
                label={`Pendientes: ${detalles.filter((d) => d.conciliado !== 'S').length}`}
                sx={{ bgcolor: FP.color.warningSoft, color: '#92400E', fontWeight: 700, fontSize: '0.7rem' }}
              />
            </Box>
          </Box>

          {detalles.length === 0 ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
              <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                No hay partidas en esta conciliación
              </Typography>
              <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }} textAlign="center" px={2}>
                Las transacciones del diario para esta cuenta y período aparecerán aquí.
              </Typography>
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                border: `1px solid ${FP.color.border}`,
                borderRadius: 2,
                overflow: 'auto',
                maxHeight: 'calc(100vh - 480px)',
              }}
            >
              <Table sx={{ tableLayout: 'fixed', width: '100%', minWidth: { xs: 980, sm: 1180 } }}>
                <colgroup>
                  {['6%', '10%', '14%', '10%', '24%', '12%', '12%', '12%'].map((w, i) => (
                    <col key={i} style={{ width: w }} />
                  ))}
                </colgroup>
                <TableBody>
                  <TableRow
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      '& td': {
                        border: 'none',
                        borderBottom: `2px solid ${FP.color.border}`,
                        bgcolor: FP.color.surfaceAlt,
                      },
                    }}
                  >
                    <TableCell padding="checkbox">
                      {esEditable && puedeModificar && (
                        <Checkbox
                          size="small"
                          checked={allChecked}
                          indeterminate={someChecked && !allChecked}
                          onChange={(e) => toggleAll(e.target.checked)}
                          sx={{ color: FP.color.navy, '&.Mui-checked': { color: FP.color.navy } }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={cellHeadSx}>
                        Fecha
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={cellHeadSx}>
                        Documento
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={cellHeadSx}>
                        Número
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={cellHeadSx}>
                        Concepto
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                        Débito
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                        Crédito
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                        Estado
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {detalles.map((d) => {
                    const checked = d.conciliado === 'S';
                    const concepto = d.concepto || d.datafiscal || '';
                    return (
                      <TableRow
                        key={d.id}
                        hover
                        sx={{
                          bgcolor: checked ? `${FP.color.successSoft}44` : 'transparent',
                          '&:hover': { bgcolor: checked ? `${FP.color.successSoft}88` : FP.color.surfaceAlt },
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={checked}
                            disabled={!esEditable || !puedeModificar}
                            onChange={(e) => togglePartida(d.id, e.target.checked)}
                            sx={{ color: FP.color.navy, '&.Mui-checked': { color: FP.color.success } }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={cellEllipsisSx} title={d.fecha || '—'}>
                          {d.fecha || '—'}
                        </TableCell>
                        <TableCell sx={cellEllipsisSx} title={d.documento || '—'}>
                          {d.documento || '—'}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: FP.color.textPrimary }} title={String(d.numero || '')}>
                          {d.numero || '—'}
                        </TableCell>
                        <TableCell sx={cellEllipsisSx} title={concepto || '—'}>
                          {concepto || '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                          {fmtMoney(d.debito)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                          {fmtMoney(d.credito)}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={checked ? 'Conciliada' : 'Pendiente'}
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.65rem',
                              bgcolor: checked ? FP.color.successSoft : FP.color.warningSoft,
                              color: checked ? FP.color.successDark : '#92400E',
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow sx={{ bgcolor: FP.color.surfaceAlt }}>
                    <TableCell
                      colSpan={5}
                      align="right"
                      sx={{ fontWeight: 700, borderBottom: 'none' }}
                    >
                      TOTALES (General / Conciliado)
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 700, fontFamily: 'monospace', fontSize: '0.85rem', borderBottom: 'none' }}
                    >
                      {fmtMoney(totalDebitos)} / {fmtMoney(totalDebConc)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 700, fontFamily: 'monospace', fontSize: '0.85rem', borderBottom: 'none' }}
                    >
                      {fmtMoney(totalCreditos)} / {fmtMoney(totalCredConc)}
                    </TableCell>
                    <TableCell sx={{ borderBottom: 'none' }} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Diálogo cerrar */}
      <Dialog open={dialogoCerrar} onClose={() => setDialogoCerrar(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 2.5, border: `1px solid ${FP.color.border}`, boxShadow: FP.shadow.md } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '0.98rem', borderBottom: `1px solid ${FP.color.border}`, bgcolor: '#FAFBFE', display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon sx={{ color: FP.color.success }} fontSize="small" />
          Cerrar conciliación
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography sx={{ fontSize: '0.9rem', color: FP.color.textPrimary }}>
            ¿Desea cerrar esta conciliación? Las partidas conciliadas se marcarán como tal en el diario.
            {detalles.some((d) => d.conciliado !== 'S') && (
              <Typography component="span" sx={{ display: 'block', mt: 1, color: FP.color.warning, fontWeight: 600 }}>
                ⚠ Hay {detalles.filter((d) => d.conciliado !== 'S').length} partida(s) pendiente(s).
              </Typography>
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, borderTop: `1px solid ${FP.color.border}`, bgcolor: '#FAFBFE' }}>
          <Button onClick={() => setDialogoCerrar(false)} sx={{ textTransform: 'none', color: FP.color.textSecondary }}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={cerrar}
            sx={{ ...botonNavySx, bgcolor: FP.color.success, '&:hover': { bgcolor: FP.color.successDark } }}>
            Confirmar cierre
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo anular */}
      <Dialog open={dialogoAnular} onClose={() => setDialogoAnular(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 2.5, border: `1px solid ${FP.color.border}`, boxShadow: FP.shadow.md } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '0.98rem', borderBottom: `1px solid ${FP.color.border}`, bgcolor: '#FAFBFE', display: 'flex', alignItems: 'center', gap: 1 }}>
          <CancelIcon sx={{ color: FP.color.danger }} fontSize="small" />
          Anular conciliación
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography sx={{ fontSize: '0.9rem', color: FP.color.textPrimary }}>
            ¿Desea anular esta conciliación? Se revertirán las marcas de conciliación en el diario. Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, borderTop: `1px solid ${FP.color.border}`, bgcolor: '#FAFBFE' }}>
          <Button onClick={() => setDialogoAnular(false)} sx={{ textTransform: 'none', color: FP.color.textSecondary }}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={anular}
            sx={{ ...botonNavySx, bgcolor: FP.color.danger, '&:hover': { bgcolor: '#B91C3C' } }}>
            Confirmar anulación
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConciliacionBancaria;
